import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Lock, Shield, Zap, Briefcase, TrendingUp, Target, Clock, Wrench, BarChart3, Plus } from "lucide-react";
import StickyCTABar from "@/components/StickyCTABar";
import CountdownTimer from "@/components/CountdownTimer";
import ExitIntentModal from "@/components/ExitIntentModal";
import { captureUTMParams } from "@/utils/utmUtils";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountManagersPage() {
  const heroRef = useRef(null);
  const [offerExpired, setOfferExpired] = useState(false);

  const navigate = useNavigate();
  const startAnalysis = () => navigate("/intake");

  // Capture UTM params on mount (Task 3)
  useEffect(() => { captureUTMParams(); }, []);

  // FAQ state
  const [faqOpen, setFaqOpen] = useState(0);

  const stats = [
    { value: "6,400+", label: "AM/CSM Resumes Analyzed" },
    { value: "3.2x", label: "More Callbacks" },
    { value: "90%", label: "ATS Pass Rate After Fix" },
    { value: "₹14.8L", label: "Avg. Placed Salary" },
  ];

  const companies = [
    { name: "Freshworks", logo: "/logos/am_freshworks.png" },
    { name: "Zoho", logo: "/logos/am_zoho.png" },
    { name: "Salesforce", logo: "/logos/am_salesforce.png" },
    { name: "Whatfix", logo: "/logos/am_whatfix.png" },
    { name: "Sprinklr", logo: "/logos/am_sprinklr.png" },
    { name: "Chargebee", logo: "/logos/am_chargebee.png" },
    { name: "HubSpot", logo: "/logos/am_hubspot.png" },
    { name: "Postman", logo: "/logos/am_postman.png" },
  ];

  const features = [
    {
      icon: <Clock className="w-5 h-5" />,
      tag: "AM/CSM Critical",
      title: "ATS Readiness Analyzer",
      desc: "SaaS hiring managers search for \"NRR,\" \"churn reduction,\" and \"Gainsight\" — not \"managed relationships.\" We scan your resume for 180+ AM/CSM-specific keywords and flag everything the ATS will miss, then score you out of 100.",
      example: "Scans for \"NRR\", \"Salesforce\", \"Gainsight\", \"QBR\", \"expansion revenue\" + 180 AM/CSM keywords"
    },
    {
      icon: <Wrench className="w-5 h-5" />,
      title: "Revenue Framing Engine",
      desc: "The #1 CSM resume mistake: sounding like support. We rewrite every bullet point to frame you as a revenue driver — with retention metrics, expansion numbers, and business impact that make hiring managers stop scrolling.",
      example: "Before: \"Managed client relationships and handled escalations\" → After: \"Retained $2.4M ARR across 45 accounts, reducing churn by 32% through proactive QBRs and health score monitoring\""
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      tag: "AM/CSM Critical",
      title: "Career Velocity Simulator",
      desc: "Stay in IT services or jump to SaaS? Target enterprise accounts or go mid-market? See how each path changes your salary over 12, 24, and 36 months — with real compensation data from Indian SaaS companies.",
      example: "Simulates: IT services AM (₹6L) → Freshworks Sr. CSM (₹18L) in 10 months"
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      title: "Smart SaaS Job Matching",
      desc: "Stop applying to generic \"relationship manager\" roles. We score every opening against your exact profile — CRM tools, account size, industry domain, B2B or B2C — and show only roles where you'll actually get responses.",
      example: "\"94% match: Enterprise CSM at Chargebee — your Salesforce + SaaS renewal exp is a direct fit\""
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      tag: "AM/CSM Critical",
      title: "CS Market Signals & Trends",
      desc: "Know which CS specializations are commanding premiums (enterprise CSMs earn 50%+ more) and which tools to master next — before you invest in the wrong certification or CRM platform.",
      example: "\"Enterprise CSM roles up 34% YoY in Bangalore\" — Gainsight + Salesforce skills carry highest premium"
    },
    {
      icon: <Target className="w-5 h-5" />,
      title: "Services-to-SaaS Transition Plan",
      desc: "Stuck in IT services doing client servicing instead of strategic CS? We map the exact gap between your current role and SaaS CSM/AM positions — and build a 30/60/90-day roadmap to bridge it.",
      example: "Gap analysis: \"Add Gainsight certification + health scoring experience → eligible for 22 more SaaS CSM openings\""
    },
  ];

  const testimonials = [
    {
      initials: "SP",
      name: "Shruti Pillai",
      img: "/avatars/shruti_pillai.jpg",
      role: "Infosys Client Partner (₹7L) → Freshworks Sr. CSM, Chennai",
      rating: 5,
      text: "I was doing client servicing at an IT services company — good clients, no metrics. PlacementOS rewrote my resume to lead with retention numbers and expansion revenue I didn't even realize I was driving. The framing change was everything. 6 callbacks in the first week.",
      result: "Placed at Freshworks — ₹18 LPA"
    },
    {
      initials: "RD",
      name: "Rohan Desai",
      img: "/avatars/rohan_desai.jpg",
      role: "Mid-market CSM → Sprinklr Enterprise CSM, Bangalore",
      rating: 5,
      text: "The career velocity simulator showed me exactly why enterprise CSMs earn 50% more — and the skills gap was just Gainsight + health scoring. I upskilled in 6 weeks, applied to matched roles, and the salary jump was immediate.",
      result: "Moved from ₹8L → ₹22L in 4 months"
    },
    {
      initials: "RM",
      name: "Ritika Malhotra",
      img: "/avatars/ritika_malhotra.jpg",
      role: "BPO Team Lead → Associate CSM, Zoho, Chennai",
      rating: 5,
      text: "Coming from a BPO background, no one was taking my applications seriously. PlacementOS restructured my resume to highlight client retention and NPS improvements from my support role. Three SaaS companies called back within two weeks.",
      result: "3 offers — accepted ₹10 LPA at Zoho"
    },
  ];

  const faqs = [
    {
      question: "What exactly do I get for ₹99?",
      answer: "A complete AM/CSM career analysis: your ATS readiness score out of 100, every blocker with exact fix recommendations written in revenue language, a services-to-SaaS career roadmap, smart job matches with fit scores across Indian and global SaaS companies, and a career velocity simulation. The free analysis shows your score and top blockers — you pay only for the full report."
    },
    {
      question: "Is this built specifically for AMs and CSMs?",
      answer: "Yes. Our keyword database covers 180+ AM/CSM-specific terms — NRR, net revenue retention, churn reduction, expansion revenue, ARR, MRR, QBR, customer health score, Gainsight, Salesforce, HubSpot, ChurnZero, Totango, onboarding, renewal, upsell, cross-sell, CSAT, NPS, and more."
    },
    {
      question: "I come from customer support / BPO. Can this help me move into CSM?",
      answer: "This is one of our most common transitions. Support professionals already have deep customer empathy, escalation management, and product knowledge — your foundation is strong. We rewrite your support experience using CS language — highlighting retention impact, NPS improvements, and adoption metrics that ATS and hiring managers look for."
    },
    {
      question: "I'm an AM at an IT services company. Can this get me into SaaS?",
      answer: "This is the transition we're best at. IT services AMs handle renewals, escalations, and client relationships — all transferable to SaaS. We reframe your experience in SaaS terms, match you to roles where your existing skills give you an edge, and show the 2–3 tool gaps to close. Typical salary jump: 2–3x."
    },
    {
      question: "How different is an AM resume from a CSM resume?",
      answer: "AMs are typically more revenue-focused (upsell, cross-sell, commercial ownership), while CSMs lean toward adoption, onboarding, and retention. Our analysis identifies which framing fits each role you're targeting — and generates separate fix recommendations if you're applying to both AM and CSM positions."
    },
    {
      question: "Is my data safe?",
      answer: "Your resume and analysis data are encrypted end-to-end, never shared with third parties, and you can request deletion at any time. Payments are processed through secure, PCI DSS Level 1 compliant infrastructure."
    },
  ];

  // Handlers
  // (upload handlers removed — using LPHeroEmailCapture)

  return (
    <div className="ap-root min-h-screen" data-testid="account-managers-page">
      {/* Exit Intent Modal */}
      <ExitIntentModal lpSlug="account-managers" />

      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="ap-mesh ap-grid-overlay relative pt-24 pb-10" style={{ overflowX: "clip" }}>
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="ap-chip ap-rise ap-rise-1 mb-6">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--ap-violet)" }}></span>
                <span className="text-sm font-medium">Built for AMs &amp; CSMs in India</span>
              </div>
              <h1 className="ap-display ap-rise ap-rise-2 text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-[var(--ap-ink)]">
                Your clients love you.<br />
                Your resume says <span className="ap-grad-text">"managed accounts."</span>
              </h1>
              <p className="ap-rise ap-rise-3 text-xl text-[var(--ap-muted)] mb-8 leading-relaxed">
                You've saved at-risk accounts, driven renewals, and expanded revenue. But your resume reads like a job description — not a revenue story. PlacementOS finds the exact blockers killing your applications at SaaS companies that pay 2–3x more.
              </p>
              <div className="ap-rise ap-rise-4 mt-6">
                <button onClick={startAnalysis} className="ap-btn ap-btn-fire ap-btn-lg" data-testid="account-managers-hero-cta">
                  Start My Free Analysis
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="relative ap-rise ap-rise-2">
              {/* floating 3D stat chips */}
              <div className="hidden md:flex ap-glass ap-float absolute -left-6 -top-5 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <span className="w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: "var(--ap-fire)" }}>88</span>
                <div><div className="text-[12px] font-bold text-[var(--ap-ink)] leading-none">Fixed score</div><div className="text-[11px] text-[var(--ap-muted)]">31 → 88 after fix</div></div>
              </div>
              <div className="hidden md:flex ap-glass ap-float-2 absolute -right-6 -bottom-6 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <Briefcase className="w-5 h-5 text-[var(--ap-violet)]" />
                <div><div className="ap-display font-extrabold text-[var(--ap-ink)] leading-none">6</div><div className="text-[11px] text-[var(--ap-muted)]">Smart Matches Found</div></div>
              </div>
              <div className="ap-card ap-tilt rounded-[26px] overflow-hidden relative z-10">
                <div className="px-4 py-3 flex items-center gap-2 border-b border-[var(--ap-line)]" style={{ background: "var(--ap-canvas)" }}>
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                  </div>
                  <span className="text-xs text-[var(--ap-muted)] ml-2">placementos.ai/csm-analysis</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 rounded-full border-4 border-red-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-red-500">31</span>
                    </div>
                    <div>
                      <h4 className="ap-display font-semibold text-[var(--ap-ink)]">ATS Readiness Score</h4>
                      <p className="text-sm text-red-500">High rejection risk — 5 critical blockers found</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Missing "NRR", "churn reduction", "Gainsight" keywords</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">No revenue metrics — "managed accounts" has zero impact</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-amber-500 mt-0.5">!</span>
                      <span className="text-[var(--ap-ink-2)]">Positioned as support, not revenue driver — wrong framing</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-[var(--ap-green)] mt-0.5">✓</span>
                      <span className="text-[var(--ap-ink-2)]">Fix available — estimated new score: 88</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12" style={{ background: "var(--ap-canvas)" }}>
        <div className="ap-section max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="ap-display text-3xl md:text-4xl font-bold text-[var(--ap-ink)] mb-2">{stat.value}</div>
                <div className="text-sm text-[var(--ap-muted)]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="py-12" style={{ background: "var(--ap-canvas-2)" }}>
        <div className="ap-section max-w-6xl text-center">
          <p className="text-[var(--ap-muted)] mb-8 font-medium">Our users have landed roles at</p>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-6">
            {companies.map((c, idx) => (
              <div key={idx} className="h-10 md:h-12 flex items-center justify-center" title={c.name}>
                <img src={c.logo} alt={c.name} loading="lazy" className="h-full w-auto object-contain max-w-[140px]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The Real Problem</p>
          <h2 className="ap-h2 ap-display mb-6 text-[var(--ap-ink)]">
            You drive revenue.<br />
            Your resume says <span className="ap-grad-text">"support."</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed mb-12">
            India's SaaS industry has 7,000+ open CSM and Account Manager roles. Freshworks, Zoho, Sprinklr, and global SaaS companies are hiring aggressively. But most AM/CSM resumes read like customer support job descriptions — "managed relationships," "handled escalations," "ensured satisfaction." No retention numbers. No expansion revenue. No NRR.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold ap-grad-text mb-2">7,000+</div>
              <p className="text-[var(--ap-ink-2)]">open CSM & Account Manager roles in India — growing 22% year-over-year</p>
            </div>
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold ap-grad-text mb-2">2–3x</div>
              <p className="text-[var(--ap-ink-2)]">salary gap between IT services AM and SaaS product company CSM</p>
            </div>
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold ap-grad-text mb-2">20+</div>
              <p className="text-[var(--ap-ink-2)]">applicants per AM/CSM opening — most resumes look identical</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The PlacementOS Way</p>
          <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-6">
            Reframe your resume.<br />
            Then land where you'll <span className="ap-grad-text">earn what you're worth.</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed">
            PlacementOS scans your resume the way Workday, Greenhouse, and Lever ATS do — flags every blocker specific to AM/CSM roles — and matches you to SaaS companies where your retention and expansion skills command premium pay.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-10" style={{ background: "var(--ap-canvas)" }}>
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>1</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Upload your resume</h3>
              <p className="text-[var(--ap-muted)]">Drop your PDF or DOCX. Our engine parses it the same way Freshworks, Salesforce, and Zoho ATS do — and flags what gets you filtered into the wrong bucket.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>2</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Get your AM/CSM diagnosis</h3>
              <p className="text-[var(--ap-muted)]">See your score, every blocker, and prioritized fixes — tuned for the exact keywords SaaS hiring managers search for: NRR, churn, expansion, QBR, health scores.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>3</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Fix, match & apply smart</h3>
              <p className="text-[var(--ap-muted)]">Apply copy-paste fixes, unlock jobs matched to your domain (SaaS, B2B, enterprise), and get a roadmap to the CSM/AM roles that pay 2–3x your current package.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10" style={{ background: "var(--ap-canvas)" }} id="features">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">What You Get</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              Every tool an AM or CSM<br />needs to <span className="ap-grad-text">get placed at top SaaS.</span>
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)]">Not a generic resume checker. Every feature understands how Account Manager and Customer Success hiring actually works.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}>
                    {feature.icon}
                  </div>
                  {feature.tag && (
                    <span className="ap-chip" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>{feature.tag}</span>
                  )}
                </div>
                <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">{feature.title}</h3>
                <p className="text-[var(--ap-muted)] mb-4">{feature.desc}</p>
                <div className="border-l-4 p-3 text-sm text-[var(--ap-ink-2)] italic rounded-r-lg" style={{ background: "var(--ap-violet-soft)", borderColor: "var(--ap-violet)" }}>
                  {feature.example}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">The Transformation</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              From "relationship manager"<br />to <span className="ap-grad-text">revenue driver.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="ap-card p-8 border-red-200" style={{ background: "#FEF2F2" }}>
              <h3 className="ap-display text-2xl font-bold mb-6 text-red-500">Before PlacementOS</h3>
              <ul className="space-y-4">
                {[
                  "Applied to 100+ CSM roles — 0 callbacks",
                  "\"Managed client relationships\" — reads like support",
                  "No retention, churn, or expansion metrics",
                  "Stuck at ₹7L doing client servicing at IT services",
                  "No idea which SaaS roles match existing skills",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                    <span className="text-red-500 text-xl">×</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="ap-card p-8" style={{ background: "#F0FDF4", borderColor: "#BBF7D0" }}>
              <h3 className="ap-display text-2xl font-bold mb-6 text-[var(--ap-green)]">After PlacementOS</h3>
              <ul className="space-y-4">
                {[
                  "Applied to 18 matched SaaS roles — got 6 callbacks",
                  "\"Retained $2.4M ARR, reduced churn by 32%\"",
                  "ATS score jumped from 31 → 88 after fixes",
                  "Clear path: IT services → SaaS CSM in 10 months",
                  "Placed as Sr. CSM at Freshworks, ₹18 LPA",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                    <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Real Results</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              AMs & CSMs who stopped<br />blending in and <span className="ap-grad-text">got placed.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <span key={i} style={{ color: "var(--ap-orange)" }}>★</span>
                  ))}
                </div>
                <p className="text-[var(--ap-ink-2)] mb-4 leading-relaxed">{t.text}</p>
                <div className="flex items-center gap-2 text-[var(--ap-green)] font-semibold mb-4">
                  <CheckCircle className="w-5 h-5" />
                  {t.result}
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-12 h-12 text-white rounded-full flex items-center justify-center font-bold overflow-hidden shrink-0" style={{ background: "var(--ap-fire)" }}>
                    {t.initials}
                    {t.img && <img src={t.img} alt={t.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => e.currentTarget.remove()} />}
                  </div>
                  <div>
                    <div className="ap-display font-semibold text-[var(--ap-ink)]">{t.name}</div>
                    <div className="text-xs text-[var(--ap-muted)]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-10" style={{ background: "var(--ap-canvas)" }}>
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-10">
            <p className="ap-eyebrow mb-4">Launch Pricing</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              One analysis. Everything you need<br />to <span className="ap-grad-text">land your SaaS role.</span>
            </h2>
            <div className="mt-4">
              <CountdownTimer onExpired={() => setOfferExpired(true)} />
            </div>
          </div>
          <div className="ap-card relative overflow-hidden p-8 md:p-12" style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }}>
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
            <div className="text-center mb-8">
              <span className="ap-chip mb-4 font-bold" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                {offerExpired ? "Price Updated" : "Launch Offer — 90% Off"}
              </span>
              <h3 className="ap-display text-3xl font-bold text-[var(--ap-ink)] mb-2">Complete AM/CSM Career Analysis</h3>
              <p className="text-[var(--ap-muted)]">ATS diagnosis + revenue framing + SaaS job matching</p>
            </div>
            <div className="space-y-3 mb-8">
              {[
                { item: "ATS Score & Full Blocker Analysis", price: "₹499" },
                { item: "Revenue Framing Rewrites", price: "₹399" },
                { item: "Services-to-SaaS Career Roadmap", price: "₹599" },
                { item: "Smart SaaS Job Matching + Fit Scores", price: "₹299" },
                { item: "Career Velocity Simulation", price: "₹99" },
              ].map((row, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                  <span className="flex items-center gap-2 text-[var(--ap-ink-2)]">
                    <CheckCircle className="w-5 h-5 text-[var(--ap-green)]" />
                    {row.item}
                  </span>
                  <span className="text-[var(--ap-muted)] line-through">{row.price}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center py-4 border-t-2 border-[var(--ap-line)] mb-8">
              <span className="text-lg font-semibold text-[var(--ap-ink)]">Total Value</span>
              <span className="text-lg font-semibold text-[var(--ap-ink)]">₹1,995</span>
            </div>
            <div className="text-center mb-8">
              <p className="text-[var(--ap-muted)] mb-2">You pay today</p>
              <div className="ap-display text-6xl font-bold text-[var(--ap-ink)]">
                {offerExpired ? (
                  <><span className="text-3xl">₹</span>499<p className="text-base text-[var(--ap-muted)] mt-2 font-normal">Offer expired</p></>
                ) : (
                  <><span className="text-3xl">₹</span>99<p className="text-[var(--ap-muted)] mt-2 text-base font-normal">One-time payment · Lifetime access</p></>
                )}
              </div>
            </div>
            <button
              onClick={startAnalysis}
              className="ap-btn ap-btn-fire ap-btn-lg w-full"
              data-testid="am-pricing-cta"
            >
              Start My Free Analysis
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-center text-[var(--ap-muted)] text-sm mt-4 flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4 text-[var(--ap-green)]" />
              Free analysis first — pay only if you want the full report
            </p>
            <div className="flex justify-center gap-6 mt-6 text-sm text-[var(--ap-muted)]">
              <span className="flex items-center gap-1"><Lock className="w-4 h-4" /> Secure</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> SSL</span>
              <span className="flex items-center gap-1"><Zap className="w-4 h-4" /> Instant</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Questions</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)]">Frequently asked.</h2>
          </div>
          <div className="mt-12 space-y-5">
            {faqs.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={i} className="ap-card overflow-hidden transition-shadow"
                  style={open ? { borderColor: "var(--ap-violet)", boxShadow: "0 18px 50px -24px rgba(91,61,245,.4)" } : {}}>
                  <button onClick={() => setFaqOpen(open ? -1 : i)} className="group w-full flex items-center justify-between gap-4 text-left px-6 py-6 bg-transparent border-0 cursor-pointer">
                    <span className="ap-display font-bold text-[16.5px] text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors">{f.question}</span>
                    <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors"
                      style={{ background: open ? "var(--ap-violet)" : "var(--ap-violet-soft)", color: open ? "#fff" : "var(--ap-violet)" }}>
                      <Plus className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden"><p className="px-6 pb-6 text-[15px] text-[var(--ap-muted)] leading-relaxed">{f.answer}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">Ready?</p>
          <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-6">
            SaaS companies are hiring AMs & CSMs.<br />
            Your resume just isn't <span className="ap-grad-text">speaking their language.</span>
          </h2>
          <p className="text-xl text-[var(--ap-ink-2)] mb-8">
            In 2 minutes, you'll know exactly what's blocking your applications — and exactly how to reframe your experience as the revenue story it actually is.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={startAnalysis}
              className="ap-btn ap-btn-fire ap-btn-lg"
            >
              Start My Free Analysis
              <ArrowRight className="w-5 h-5" />
            </button>
            <a href="#features" className="ap-btn ap-btn-ghost ap-btn-lg">
              See All Features
            </a>
          </div>
          <div className="mt-6">
            <a href="/find-jobs" className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink-2)] underline underline-offset-2">
              Or find a free job →
            </a>
          </div>
        </div>
      </section>

      {/* Sticky CTA Bar */}
      <StickyCTABar heroRef={heroRef} onCTAClick={startAnalysis} />
    </div>
  );
}
